import { useState } from "react";
import { showToast } from "../../../../core/utils/alert";
import {
  usePromoteStudentMutation,
  usePromoteStudentsBulkMutation,
} from "../api/promotionApi";
import {
  SimplePromotionPayloadType,
  SimpleBulkPromotionPayloadType,
} from "../types/promotionRequest";

export const usePromotion = (params?: {
  session_id?: string;
  course_id?: string;
  from_stage_id?: string;
}) => {
  const [isLoading, setIsLoading] = useState(false);

  // RTK Query hooks
  const [promoteStudentMutation, { isLoading: isPromoting }] =
    usePromoteStudentMutation();
  const [promoteStudentsBulkMutation, { isLoading: isPromotingBulk }] =
    usePromoteStudentsBulkMutation();

  // Handle single student promotion
  const promoteStudent = async (studentId: number) => {
    try {
      const payload: SimplePromotionPayloadType = {
        student_id: studentId,
      };

      const response = await promoteStudentMutation(payload).unwrap();
      showToast({
        title: "Success",
        text: "Student promoted successfully",
        icon: "success",
      });
      return { success: true, data: response.data };
    } catch (error: any) {
      showToast({
        title: "Error",
        text: error.data?.message || "Failed to promote student",
        icon: "error",
      });
      return { success: false, error };
    }
  };

  // Handle bulk student promotion
  const promoteStudentsBulk = async (studentIds: number[]) => {
    try {
      const payload: SimpleBulkPromotionPayloadType = {
        student_ids: studentIds,
      };

      const response = await promoteStudentsBulkMutation(payload).unwrap();
      showToast({
        title: "Success",
        text: `${response.data.count} students promoted successfully`,
        icon: "success",
      });
      return { success: true, data: response.data };
    } catch (error: any) {
      showToast({
        title: "Error",
        text: error.data?.message || "Failed to promote students",
        icon: "error",
      });
      return { success: false, error };
    }
  };

  // Return the promotion state and functions
  const getPromotionState = () => ({
    isLoading,
    isPromoting,
    isPromotingBulk,
  });

  return {
    getPromotionState,
    promoteStudent,
    promoteStudentsBulk,
    isPromoting,
    isPromotingBulk,
  };
};
